import { FC } from "react";
import { useTheme } from "app/shared";
import styles from "./ClaimMaintenance.module.scss";

// Twitter (X) and Discord SVG icons
const TwitterIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const DiscordIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.39-.444.883-.608 1.27-.184.02-.367.046-.55.077-1.836-.222-3.653-.222-5.442 0-.183-.031-.366-.057-.55-.077-.164-.387-.398-.88-.608-1.27a.077.077 0 0 0-.079-.036c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055c1.999 1.464 3.936 2.355 5.832 2.942a.082.082 0 0 0 .088-.028c.486-.66.919-1.356 1.289-2.086a.075.075 0 0 0-.041-.106 14.518 14.518 0 0 1-2.072-1 .077.077 0 0 1-.008-.128c.14-.104.279-.213.412-.322a.074.074 0 0 1 .078-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.134.11.273.218.413.322.044.034.04.1-.006.127-.663.38-1.35.71-2.072.999a.075.075 0 0 0-.041.107c.375.73.808 1.425 1.289 2.085.02.029.056.041.088.029 1.901-.587 3.838-1.478 5.837-2.942a.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.029zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" />
  </svg>
);

const TelegramIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.820 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const ClaimMaintenance: FC = () => {
  const { theme } = useTheme();

  return (
    <div className={styles.maintenanceContainer}>
      <div
        className={`${styles.content} ${
          theme === "dark" ? styles.darkTheme : ""
        }`}
      >
        <h1 className={styles.title}>Claim Portal Under Maintenance</h1>

        <p className={styles.message}>
          Our Claim Portal is currently undergoing scheduled maintenance.
        </p>

        <p className={styles.highlight}>
          ⚡ Your allocations, progress and purchase records are safe.
        </p>

        <p className={styles.message}>
          While our dev team is fixing the bugs, token distributions for new
          projects are handled directly by the project teams or via their own
          claim pages. We will always notify the community through our channels
          whenever a distribution takes place.
        </p>

        <div className={styles.helpSection}>
          <h2 className={styles.helpTitle}>Need help?</h2>
          <ul className={styles.helpList}>
            <li>
              Open a support ticket in{" "}
              <a
                href="https://discord.com/invite/NB4hhuXkWz"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.inlineLink}
              >
                Discord
              </a>
              #support
            </li>
            <li>
              Reach out in{" "}
              <a
                href="https://t.me/alphamind_community"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.inlineLink}
              >
                TelegramSupport
              </a>{" "}
              — moderators are happy to assist anytime
            </li>
          </ul>
        </div>

        <div className={styles.ctaButtons}>
          <a
            href="https://discord.com/invite/NB4hhuXkWz"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaButton}
          >
            <span className={styles.icon}>
              <DiscordIcon />
            </span>
            Open Discord Ticket
          </a>
          <a
            href="https://t.me/alphamind_community"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaButton}
          >
            <span className={styles.icon}>
              <TelegramIcon />
            </span>
            Telegram Community Support
          </a>
          <a
            href="https://x.com/alphamind_labs"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaButton}
          >
            <span className={styles.icon}>
              <TwitterIcon />
            </span>
            X Announcements
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClaimMaintenance;
